/**
 * ════════════════════════════════════════════════════════════════════════════
 *  WHICH INPUT THE USER IS ON: pointer type and last key
 * ════════════════════════════════════════════════════════════════════════════
 *
 * The tooltip layer needs two answers that no single event carries:
 *
 *   - `lastPointerType` — rc-trigger opens a hover tooltip on the emulated
 *     `pointerenter` a TAP produces, and the bubble then sticks on a phone.
 *     A hover tooltip opens only when the last pointer was a mouse.
 *   - `lastInput` — keyboard focus opens a tooltip at once, a click's focus
 *     and a focus returned after Esc/Enter do not. Only a navigation key
 *     (Tab, arrows, Home/End, PageUp/PageDown) counts as "keyboard focus".
 *
 * Document CAPTURE listeners run before React's root-delegated handlers, so
 * the answer is already known when rc-trigger calls `onOpenChange(true)`.
 * Compatibility mouse events after a tap are not pointer events, so a tap
 * leaves `lastPointerType = 'touch'`.
 *
 * The state lives on a `globalThis` symbol rather than in module scope: the
 * `./testing` subpath resets it from a separate entry point, and two copies of
 * this module (a consumer that bundles the kit twice) must still agree.
 * Imports nothing — `./testing` reaches this file and its graph must stay empty.
 */
const NAV_KEYS = new Set([
    'Tab',
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'Home',
    'End',
    'PageUp',
    'PageDown',
]);
const STATE_KEY = Symbol.for('bookora.ui.input-modality');
function state() {
    const holder = globalThis;
    let current = holder[STATE_KEY];
    if (!current) {
        current = { lastPointerType: 'mouse', lastInput: 'none', installs: new WeakMap() };
        holder[STATE_KEY] = current;
    }
    return current;
}
/** Read-only view of the current modality. */
export const inputModality = {
    get lastPointerType() {
        return state().lastPointerType;
    },
    get lastInput() {
        return state().lastInput;
    },
};
/**
 * Install the capture listeners on `doc`. Ref-counted: every caller gets a
 * disposer, and the listeners go away when the last one is disposed.
 */
export function installInputModality(doc) {
    const target = doc ?? (typeof document === 'undefined' ? undefined : document);
    if (!target)
        return () => undefined;
    const s = state();
    const existing = s.installs.get(target);
    if (existing) {
        existing.count += 1;
    }
    else {
        const onPointer = (event) => {
            const type = event.pointerType;
            s.lastPointerType = type ? type : 'mouse';
            if (event.type === 'pointerdown')
                s.lastInput = 'pointer';
        };
        const onKeyDown = (event) => {
            s.lastInput = NAV_KEYS.has(event.key) ? 'nav-key' : 'other-key';
        };
        target.addEventListener('pointerover', onPointer, true);
        target.addEventListener('pointerdown', onPointer, true);
        target.addEventListener('keydown', onKeyDown, true);
        s.installs.set(target, {
            count: 1,
            detach: () => {
                target.removeEventListener('pointerover', onPointer, true);
                target.removeEventListener('pointerdown', onPointer, true);
                target.removeEventListener('keydown', onKeyDown, true);
            },
        });
    }
    let disposed = false;
    return () => {
        if (disposed)
            return;
        disposed = true;
        const entry = s.installs.get(target);
        if (!entry)
            return;
        entry.count -= 1;
        if (entry.count <= 0) {
            entry.detach();
            s.installs.delete(target);
        }
    };
}
/** Test seam: forget the last pointer type and key (listeners stay installed). */
export function resetInputModality() {
    const s = state();
    s.lastPointerType = 'mouse';
    s.lastInput = 'none';
}
//# sourceMappingURL=input-modality.js.map